import { Injectable } from '@angular/core';
import { Observable, merge } from 'rxjs';
import { VehicleService } from './vehicle.service';
import { DeviceService } from 'src/app/vehicles/service/device-service.service';
import { map, switchMap } from 'rxjs/operators';
import { DeviceStatusService } from 'src/app/vehicles/service/device-status.service';
import { Device } from 'src/app/vehicles/model/device';
import { DeviceWithStatus } from 'src/app/vehicles/model/device-with-status';
import { VehicleDevicesWithStatus } from 'src/app/vehicles/model/vehicle-devices-with-status';
import { AdditionalInfoImpl } from 'src/app/vehicles/model/status/additional-info-impl';
import { WebsocketClient } from 'src/app/common/service/websocket-client.service';
import { DeviceStatusResponse } from 'src/app/vehicles/model/device-status-ws-response';
import { plainToClass } from 'class-transformer';
import { Group } from '../model/group';

@Injectable()
export class VehicleFacadeService {
    constructor(
        private vehicleService: VehicleService,
        private deviceService: DeviceService,
        private deviceStatusService: DeviceStatusService,
        private websocket: WebsocketClient
    ) { }

    subscribeToUserDeviceStatuses(groups: Group[]): Observable<VehicleDevicesWithStatus> {
        return merge(
            ...groups.map(group =>
                this.websocket.subscribe(`/topic/group/${group.name}/statuses`).pipe(
                    map((msg: any) => {
                        const payload: DeviceStatusResponse = msg;
                        const statusList: AdditionalInfoImpl[] = plainToClass(AdditionalInfoImpl, payload.status);
                        return new VehicleDevicesWithStatus(
                            payload.sideNumber,
                            false,
                            statusList.map(s => new DeviceWithStatus(s.device, s))
                        );
                    })
                )
            )
        );
    }

    unsubscribe(): void {
        this.websocket.unsubscribe('/user/queue/statuses');
    }

    fetchVehiclesWithDevices(): Observable<VehicleDevicesWithStatus[]> {
        return this.vehicleService.fetchVehicles().pipe(
            switchMap(vehicles => {
                const sideNumbers = vehicles.map(v => v.sideNumber);

                return this.deviceService.getVehiclesDevices(sideNumbers).pipe(
                    switchMap(vehicleDevicesList => {
                        const allDevices: Device[] = vehicleDevicesList.reduce(
                            (acc, vd) => acc.concat(vd.devices),
                            [] as Device[]
                        );

                        return this.deviceStatusService.getVehiclesDevicesStatuses(allDevices).pipe(
                            map((statuses: AdditionalInfoImpl[]) =>
                                vehicleDevicesList.map(vehicleDevices => {
                                    const devicesWithStatus: DeviceWithStatus[] = vehicleDevices.devices.map(device => {
                                        const status = statuses.find(
                                            s => s.device && s.device.devId === device.devId
                                        );
                                        return new DeviceWithStatus(device, status || null);
                                    });
                                    const foundVehicle = vehicles.find(vehicle => vehicle.sideNumber === vehicleDevices.sideNumber);
                                    return new VehicleDevicesWithStatus(vehicleDevices.sideNumber, foundVehicle.onDepot, devicesWithStatus);
                                })
                            )
                        );
                    })
                );
            })
        );
    }
}
